import { createOnlineClient, type OnlineClient } from "../net/net";
import { Renderer, type GameSnapshot, type SnapshotEntity } from "../render/renderer";
import { createGameScreen, replaceRoot } from "../ui/ui";
import { GameLoop } from "./loop";
import { SnapshotInterpolator } from "./snapshot_interpolator";
import {
  cleanup,
  createGameplayUi,
  createInput,
  createState,
  extractDefeat,
  extractGameEnd,
  extractServerErrorDetails,
  extractSnapshot,
  GameplayAudio,
  type GameSession,
  type SessionOptions
} from "./session_helpers";
import {
  clearQueueOverlay,
  showQueueOverlay,
  syncDefeatOverlay,
  syncGameEndOverlay,
  syncUpgradeOverlay,
  toggleGameMenuOverlay
} from "./session_overlays";

const ONLINE_INPUT_SEND_INTERVAL_MS = 60;
const ONLINE_INPUT_CHANGE_MIN_INTERVAL_MS = 16;
const ONLINE_INPUT_KEEPALIVE_INTERVAL_MS = ONLINE_INPUT_SEND_INTERVAL_MS * 4;
const INPUT_CHANGE_EPSILON = 0.002;
const LOCAL_PLAYER_DEFAULT_SPEED = 300;
const LOCAL_PLAYER_RECONCILE_BLEND_IDLE = 0.35;
const LOCAL_PLAYER_RECONCILE_BLEND_MOVING = 0.08;
const LOCAL_PLAYER_SNAP_DISTANCE = 180;

type OnlineInputState = {
  lastSentAt: number;
  lastX: number;
  lastY: number;
  hasSent: boolean;
};

type Vec2 = {
  x: number;
  y: number;
};

export async function startOnlineSession(options: SessionOptions): Promise<GameSession> {
  const state = createState();
  const gameplayAudio = new GameplayAudio(options);
  const onlineInput: OnlineInputState = {
    lastSentAt: 0,
    lastX: 0,
    lastY: 0,
    hasSent: false
  };
  const localPrediction = new LocalPlayerPrediction();

  state.snapshotInterpolator = new SnapshotInterpolator();

  const screen = createGameScreen(options.i18n.t("status.connecting"));
  replaceRoot(options.root, screen.element);

  const renderer = new Renderer({ canvas: screen.canvas });
  state.renderer = renderer;

  const client = createOnlineClient({
    onStatusChange: (status) => {
      if (!state.currentSnapshot && !state.queueOverlay && !state.gameEndResult) {
        screen.setStatus(status === "connected" ? options.i18n.t("status.connecting") : status);
      }
    },
    onMessage: (message) => {
      const gameEnd = extractGameEnd(message);

      if (gameEnd) {
        state.gameEndResult = gameEnd;
        state.defeatTimeoutMs = null;
        state.currentSnapshot = null;
        state.renderSnapshot = null;
        state.hasMoved = false;
        state.menuOpen = false;
        state.respawning = false;
        state.snapshotInterpolator?.clear();
        localPrediction.clear();
        clearQueueOverlay(state);
        screen.setStatus("");
        renderer.render(null, createGameplayUi(options, state));
        return;
      }

      const defeat = extractDefeat(message);

      if (defeat) {
        state.defeatTimeoutMs = defeat.timeoutMs;
        state.currentSnapshot = createDefeatedSnapshot(state.currentSnapshot);
        state.renderSnapshot = state.currentSnapshot;
        state.hasMoved = false;
        state.menuOpen = false;
        state.respawning = false;
        state.snapshotInterpolator?.clear();
        localPrediction.clear();
        clearQueueOverlay(state);
        screen.setStatus("");
        renderer.render(state.renderSnapshot, createGameplayUi(options, state));
        return;
      }

      const snapshot = extractSnapshot(message);

      if (snapshot) {
        state.currentSnapshot = snapshot;
        state.snapshotInterpolator?.push(snapshot);
        localPrediction.reconcile(snapshot);
        state.respawning = false;
        clearQueueOverlay(state);
        screen.setStatus("");
        return;
      }

      const error = extractServerErrorDetails(message);

      if (!error) {
        return;
      }

      if (error.code === "queue") {
        showQueueOverlay(options, state, error.message);
        screen.setStatus("");
        return;
      }

      if (error.code === "queue_joining") {
        clearQueueOverlay(state);
        screen.setStatus(error.message);
        return;
      }

      if (error.message !== "TooManyInputs") {
        screen.setStatus(error.message);
      }
    },
    onError: (error) => {
      if (!state.currentSnapshot && !state.gameEndResult) {
        screen.setStatus(error.message);
      }
    }
  });

  state.onlineClient = client;
  await client.connect(options.profile);

  const input = createInput(screen.canvas, renderer, state);
  state.input = input;
  input.attach();

  window.addEventListener("keydown", onMenuKeyDown, { passive: false });
  window.addEventListener("resize", onResize);

  state.loop = new GameLoop((frame) => {
    const movement = input.update();
    const canMove =
      Boolean(state.currentSnapshot) &&
      movement.active &&
      !state.menuOpen &&
      !state.queueOverlay &&
      !state.currentSnapshot?.defeated &&
      !state.respawning &&
      !state.gameEndResult;
    const inputX = canMove ? movement.x : 0;
    const inputY = canMove ? movement.y : 0;

    sendOnlineInput(client, onlineInput, inputX, inputY);
    localPrediction.step(inputX, inputY, frame.deltaMs);

    state.renderSnapshot = applyLocalPrediction(
      state.snapshotInterpolator?.sample() ?? state.currentSnapshot,
      state.currentSnapshot,
      localPrediction.position()
    );

    const audioEvents = renderer.render(state.renderSnapshot, createGameplayUi(options, state));
    gameplayAudio.play(audioEvents);
    syncGameEndOverlay(options, state);
    syncUpgradeOverlay(options, state, "online");
    syncDefeatOverlay(options, state, "online");
  });

  state.loop.start();

  return {
    stop: () => {
      cleanup(state);
      localPrediction.clear();
      window.removeEventListener("keydown", onMenuKeyDown);
      window.removeEventListener("resize", onResize);
    }
  };

  function onMenuKeyDown(event: KeyboardEvent): void {
    if (event.code !== "Escape") {
      return;
    }

    event.preventDefault();
    toggleGameMenuOverlay(options, state);
  }

  function onResize(): void {
    renderer.resize();
  }
}

function createDefeatedSnapshot(snapshot: GameSnapshot | null): GameSnapshot {
  if (snapshot) {
    return {
      ...snapshot,
      defeated: true,
      pending_upgrades: [],
      pendingUpgrades: []
    };
  }

  return {
    defeated: true,
    entities: [],
    pending_upgrades: [],
    pendingUpgrades: []
  };
}

function sendOnlineInput(
  client: OnlineClient,
  inputState: OnlineInputState,
  x: number,
  y: number
): void {
  const now = performance.now();
  const elapsed = now - inputState.lastSentAt;
  const changed =
    Math.abs(x - inputState.lastX) > INPUT_CHANGE_EPSILON ||
    Math.abs(y - inputState.lastY) > INPUT_CHANGE_EPSILON;

  if (inputState.hasSent) {
    if (changed && elapsed < ONLINE_INPUT_CHANGE_MIN_INTERVAL_MS) {
      return;
    }

    if (!changed && elapsed < ONLINE_INPUT_KEEPALIVE_INTERVAL_MS) {
      return;
    }
  }

  client.sendInput(x, y);

  inputState.lastSentAt = now;
  inputState.lastX = x;
  inputState.lastY = y;
  inputState.hasSent = true;
}

function applyLocalPrediction(
  interpolated: GameSnapshot | null,
  latest: GameSnapshot | null,
  predictedPosition: Vec2 | null
): GameSnapshot | null {
  if (!interpolated || !latest || !predictedPosition) {
    return interpolated;
  }

  const localId = extractId(latest.local_player_id ?? latest.localPlayerId);

  if (localId === null) {
    return interpolated;
  }

  const localKey = String(localId);
  const localEntity = findOwnedPlayerEntity(interpolated, localKey);

  if (!localEntity) {
    return interpolated;
  }

  const delta = {
    x: predictedPosition.x - localEntity.position.x,
    y: predictedPosition.y - localEntity.position.y
  };

  return {
    ...interpolated,
    entities: interpolated.entities.map((entity) => {
      const kind = normalizedKind(entity.kind);
      const owner = ownerKey(entity.owner);

      if (owner !== localKey) {
        return entity;
      }

      if (isPlayerKind(kind) || isAttachedWeaponKind(kind)) {
        return {
          ...entity,
          position: {
            x: entity.position.x + delta.x,
            y: entity.position.y + delta.y
          }
        };
      }

      return entity;
    })
  };
}

class LocalPlayerPrediction {
  private predicted: Vec2 | null = null;
  private lastServerPosition: Vec2 | null = null;
  private lastServerTime = 0;
  private speed = LOCAL_PLAYER_DEFAULT_SPEED;
  private moving = false;

  public reconcile(snapshot: GameSnapshot): void {
    const entity = findLocalPlayerEntity(snapshot);

    if (!entity) {
      this.clear();
      return;
    }

    const now = performance.now();
    const serverPosition = {
      x: entity.position.x,
      y: entity.position.y
    };

    if (this.lastServerPosition && this.lastServerTime > 0) {
      const elapsedSeconds = Math.max(0.001, (now - this.lastServerTime) / 1000);
      const distance = distanceBetween(serverPosition, this.lastServerPosition);
      const measuredSpeed = distance / elapsedSeconds;

      if (Number.isFinite(measuredSpeed) && measuredSpeed >= 60 && measuredSpeed <= 900) {
        this.speed = lerp(this.speed, measuredSpeed, 0.25);
      }
    }

    if (!this.predicted) {
      this.predicted = serverPosition;
    } else {
      const correctionDistance = distanceBetween(this.predicted, serverPosition);

      if (correctionDistance > LOCAL_PLAYER_SNAP_DISTANCE) {
        this.predicted = serverPosition;
      } else {
        const blend = this.moving
          ? LOCAL_PLAYER_RECONCILE_BLEND_MOVING
          : LOCAL_PLAYER_RECONCILE_BLEND_IDLE;

        this.predicted = {
          x: lerp(this.predicted.x, serverPosition.x, blend),
          y: lerp(this.predicted.y, serverPosition.y, blend)
        };
      }
    }

    this.lastServerPosition = serverPosition;
    this.lastServerTime = now;
  }

  public step(x: number, y: number, deltaMs: number): void {
    if (!this.predicted) {
      return;
    }

    const length = Math.hypot(x, y);

    this.moving = length > 0.0001;

    if (!this.moving) {
      return;
    }

    const seconds = Math.max(0, deltaMs) / 1000;
    const normalizedX = x / length;
    const normalizedY = y / length;

    this.predicted = {
      x: this.predicted.x + normalizedX * this.speed * seconds,
      y: this.predicted.y + normalizedY * this.speed * seconds
    };
  }

  public position(): Vec2 | null {
    return this.predicted;
  }

  public clear(): void {
    this.predicted = null;
    this.lastServerPosition = null;
    this.lastServerTime = 0;
    this.speed = LOCAL_PLAYER_DEFAULT_SPEED;
    this.moving = false;
  }
}

function findLocalPlayerEntity(snapshot: GameSnapshot): SnapshotEntity | null {
  const localId = extractId(snapshot.local_player_id ?? snapshot.localPlayerId);

  if (localId === null) {
    return null;
  }

  return findOwnedPlayerEntity(snapshot, String(localId));
}

function findOwnedPlayerEntity(snapshot: GameSnapshot, ownerId: string): SnapshotEntity | null {
  return (
    snapshot.entities.find((entity) => {
      const kind = normalizedKind(entity.kind);
      const owner = ownerKey(entity.owner);

      return isPlayerKind(kind) && owner === ownerId;
    }) ?? null
  );
}

function isPlayerKind(kind: string): boolean {
  return kind === "player" || kind === "bot";
}

function isAttachedWeaponKind(kind: string): boolean {
  return kind === "damagezone" || kind === "orbitingball";
}

function normalizedKind(kind: unknown): string {
  return String(kind).replace(/[_-]/g, "").toLowerCase();
}

function ownerKey(value: unknown): string | null {
  const id = extractId(value);
  return id === null ? null : String(id);
}

function extractId(value: unknown): number | string | null {
  if (typeof value === "number" || typeof value === "string") {
    return value;
  }

  if (value && typeof value === "object" && "0" in value) {
    const nested = (value as { 0?: unknown })[0];

    if (typeof nested === "number" || typeof nested === "string") {
      return nested;
    }
  }

  return null;
}

function distanceBetween(a: Vec2, b: Vec2): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function lerp(a: number, b: number, amount: number): number {
  return a + (b - a) * amount;
}